import cn from 'classnames';
import { Dispatch, useCallback, useEffect, useRef } from 'react';
import { FormRenderProps } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { useDispatch } from 'react-redux';
import { setHours, setMinutes } from 'date-fns';

import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import {
  ISystemInoperabilityForm,
  ISystemInoperabilityFormRef,
} from 'APIPackage/SystemInoperabilityModule/interfaces/ISystemInoperability';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';

import { adminSetSystemInoperabilityFormElementRef, adminSetSystemInoperabilityFormRef } from '../actions/adminAction';
import styles from './NewsTable.module.scss';
import tableStyles from './SystemInoperabilityTable.module.scss';

export const RenderForm = ({ handleSubmit, submitting, form }: FormRenderProps<ISystemInoperabilityForm>) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const setFormRef = useCallback(
    (formRef: ISystemInoperabilityFormRef) => dispatch(adminSetSystemInoperabilityFormRef(formRef)),
    [dispatch],
  );
  const setFormElementRef = useCallback(
    (formElementRef: HTMLFormElement | null) => dispatch(adminSetSystemInoperabilityFormElementRef(formElementRef)),
    [dispatch],
  );
  const formRef = useRef(form);
  const formElementRef = useRef<HTMLFormElement>(null);
  const previousValues = useRef<Record<string, Date | null>>({});

  const closeButtonClick = useCallback((fields, i) => {
    const startFieldName = `records[${i}].startDate`;
    const endFieldName = `records[${i}].endDate`;

    delete previousValues.current[startFieldName];
    delete previousValues.current[endFieldName];

    fields.remove(i);
  }, []);

  formRef.current = form;
  const fullWidthCellClassName = cn(
    tableStyles.Cell,
    styles.TbodyCell,
    styles.TbodyRow,
    tableStyles.FullWidthCell,
    tableStyles.AddRecordRow,
  );

  useEffect(() => {
    setFormRef(formRef);
    setFormElementRef(formElementRef.current);
  }, [setFormRef, setFormElementRef, formRef, formElementRef]);

  const handleStartDateCalendarOpen = useCallback((fieldName: string) => {
    return () => {
      const now = new Date();
      const dateWithDefaultTime = setMinutes(setHours(now, 0), 0);

      previousValues.current[fieldName] = dateWithDefaultTime;

      return dateWithDefaultTime;
    };
  }, []);

  const handleEndDateCalendarOpen = useCallback((fieldName: string) => {
    return () => {
      const now = new Date();
      const dateWithDefaultTime = setMinutes(setHours(now, 23), 59);

      previousValues.current[fieldName] = dateWithDefaultTime;

      return dateWithDefaultTime;
    };
  }, []);

  const createParseStartDate = useCallback((fieldName: string) => {
    return (value: string | Date) => {
      if (!value) {
        return value;
      }

      const date = typeof value === 'string' ? new Date(value) : value;

      if (Number.isNaN(date.getTime())) {
        return value;
      }

      if (date.getHours() === 0 && date.getMinutes() === 0) {
        previousValues.current[fieldName] = date;

        return value;
      }

      const prevDate = previousValues.current[fieldName];

      if (
        !prevDate ||
        prevDate.getFullYear() !== date.getFullYear() ||
        prevDate.getMonth() !== date.getMonth() ||
        prevDate.getDate() !== date.getDate()
      ) {
        const newDate = setMinutes(setHours(date, 0), 0);

        previousValues.current[fieldName] = newDate;

        return newDate;
      }

      previousValues.current[fieldName] = date;

      return value;
    };
  }, []);

  const createParseEndDate = useCallback((fieldName: string) => {
    return (value: string | Date) => {
      if (!value) {
        return value;
      }

      const date = typeof value === 'string' ? new Date(value) : value;

      if (Number.isNaN(date.getTime())) {
        return value;
      }

      if (date.getHours() === 23 && date.getMinutes() === 59) {
        previousValues.current[fieldName] = date;

        return value;
      }

      const prevDate = previousValues.current[fieldName];

      if (
        !prevDate ||
        prevDate.getFullYear() !== date.getFullYear() ||
        prevDate.getMonth() !== date.getMonth() ||
        prevDate.getDate() !== date.getDate()
      ) {
        const newDate = setMinutes(setHours(date, 23), 59);

        previousValues.current[fieldName] = newDate;

        return newDate;
      }

      previousValues.current[fieldName] = date;

      return value;
    };
  }, []);

  return (
    <>
      <form onSubmit={handleSubmit} id="systemInoperabilityAddRecordForm" ref={formElementRef}>
        <FieldArray name="records">
          {({ fields }) =>
            fields.map((name, i) => (
              <div className={fullWidthCellClassName} key={name}>
                <DatePickerField
                  dateFormat={DATE_FORMAT_LONG_FNS}
                  timeFormat={true}
                  name={`${name}.startDate`}
                  disabled={submitting}
                  className={tableStyles.FormItem}
                  parse={createParseStartDate(`${name}.startDate`)}
                  onCalendarOpen={handleStartDateCalendarOpen(`${name}.startDate`)}
                />
                <DatePickerField
                  dateFormat={DATE_FORMAT_LONG_FNS}
                  timeFormat={true}
                  name={`${name}.endDate`}
                  disabled={submitting}
                  className={tableStyles.FormItem}
                  parse={createParseEndDate(`${name}.endDate`)}
                  onCalendarOpen={handleEndDateCalendarOpen(`${name}.endDate`)}
                />
                <div className={tableStyles.ResetCell}>
                  <TextareaField
                    resize="none"
                    name={`${name}.reason`}
                    disabled={submitting}
                    className={tableStyles.ReasonField}
                  />
                  <div onClick={() => closeButtonClick(fields, i)} className={tableStyles.RemoveRecordButton}>
                    <CloseButton className="shared-deleting-row-btn" />
                  </div>
                </div>
              </div>
            ))
          }
        </FieldArray>
      </form>
    </>
  );
};
